/**
 * The NAV write lock in the «Approval rules» tab (masterplan credit-limits-v3, C2; E12, §2). A wrong password of the
 * NAV user `dolder` sets the lock (`navWriteLockedAt`), and NAV locks the account after a few more wrong attempts, which
 * would stop the nightly run. Everybody with the page right sees that the lock is on; only Finance sees the reason and
 * can lift it, after a confirmation that names the password check. Nothing is shown while there is no lock.
 */
import { useState } from 'react';
import type { CreditLimitConfig } from '../../../types/creditLimits';
import { OverviewDialog } from '../overview/OverviewDialog';
import { apiErrorText, formatDateTime } from '../requests/requestRules';

interface Props {
    config: CreditLimitConfig;
    /** Finance and the rules tables exist (`canEditRules`). */
    editable: boolean;
    /** Lifts the lock; rejects with the API's error. */
    onUnlock: (confirmPasswordUpdated?: boolean) => Promise<unknown>;
}

export function NavLockBlock({ config, editable, onUnlock }: Props) {
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // The API answers 409 UNLOCK_COOLDOWN to a repeated unlock: Finance must then confirm the password step.
    const [needsPasswordConfirm, setNeedsPasswordConfirm] = useState(false);
    const [passwordConfirmed, setPasswordConfirmed] = useState(false);
    const { navWriteLockedAt, navWriteLockedReason, navAuthFailedAt } = config.settings;
    // C3a: a kept 'auth-failed:' marker blocks NAV even without a saved lock; the API sends its time to Finance only.
    if (!navWriteLockedAt && !navAuthFailedAt) return null;

    const unlock = async () => {
        setBusy(true);
        setError(null);
        try {
            await onUnlock(needsPasswordConfirm ? true : undefined);
            setConfirming(false);
            setNeedsPasswordConfirm(false);
            setPasswordConfirmed(false);
        } catch (err) {
            if ((err as { code?: string } | null)?.code === 'UNLOCK_COOLDOWN' && !needsPasswordConfirm) {
                setNeedsPasswordConfirm(true);
                setPasswordConfirmed(false);
                return;
            }
            setError(apiErrorText(err, 'NAV writing could not be unlocked.'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <section style={{ marginBottom: 28 }}>
            <div className="banner attention" role="status">
                {navWriteLockedAt ? (
                    <p>
                        <b>NAV writing is locked</b> since {formatDateTime(navWriteLockedAt)}. The app does not write credit limits into NAV
                        until Finance unlocks it.
                        {editable && navWriteLockedReason ? ` Reason: ${navWriteLockedReason}` : ''}
                    </p>
                ) : (
                    <p>
                        <b>NAV access is blocked</b> since {formatDateTime(navAuthFailedAt)}. NAV refused the last signed-in call and the
                        lock could not be saved, so the app sends nothing to NAV until Finance unlocks it.
                    </p>
                )}
                {editable && (
                    <div className="actions">
                        <button type="button" className="btn secondary sm" onClick={() => { setError(null); setNeedsPasswordConfirm(false); setPasswordConfirmed(false); setConfirming(true); }}>Unlock NAV writing</button>
                    </div>
                )}
            </div>
            {confirming && (
                <OverviewDialog
                    title="Unlock NAV writing"
                    onClose={() => { if (!busy) setConfirming(false); }}
                    width={560}
                    footer={(
                        <>
                            <button type="button" className="btn secondary sm" onClick={() => setConfirming(false)} disabled={busy}>Cancel</button>
                            <button type="button" className="btn primary sm" onClick={unlock} disabled={busy || (needsPasswordConfirm && !passwordConfirmed)}>{busy ? 'Unlocking…' : 'Unlock NAV writing'}</button>
                        </>
                    )}
                >
                    <p style={{ marginTop: 0 }}>
                        <b>First check the password of the NAV user dolder.</b> If it is still wrong, NAV locks the account after a few
                        more attempts and the nightly run stops.
                    </p>
                    {needsPasswordConfirm && (
                        <div className="banner attention" role="status" style={{ marginTop: 12 }}>
                            <p>NAV writing was unlocked a short time ago and is blocked again. Unlock once more only if the password is really fixed.</p>
                            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                <input type="checkbox" checked={passwordConfirmed} onChange={e => setPasswordConfirmed(e.target.checked)} />
                                <span>Password updated in the app settings and the app restarted</span>
                            </label>
                        </div>
                    )}
                    <p style={{ marginBottom: 0 }}>Unlock only after the password has been checked or set again in the App Settings.</p>
                    {error && <div className="banner error" role="alert" style={{ marginTop: 12 }}><p>{error}</p></div>}
                </OverviewDialog>
            )}
        </section>
    );
}
